/**
 * Shared visual helpers used by the Super-Admin pages to render tenants
 * consistently — deterministic colors, initials, usage stats etc.
 */

// Cores determinísticas por tenant.id — toda página exibe a mesma cor para
// o mesmo tenant, em qualquer combinação de listas/filtros.
const TENANT_PALETTE = [
  {
    bg: "from-violet-500 to-fuchsia-500",
    chip: "bg-violet-500/15 text-violet-200 border-violet-500/30",
    solid: "#8b5cf6",
  },
  {
    bg: "from-sky-500 to-cyan-400",
    chip: "bg-sky-500/15 text-sky-200 border-sky-500/30",
    solid: "#0ea5e9",
  },
  {
    bg: "from-emerald-500 to-teal-400",
    chip: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
    solid: "#10b981",
  },
  {
    bg: "from-amber-500 to-orange-400",
    chip: "bg-amber-500/15 text-amber-200 border-amber-500/30",
    solid: "#f59e0b",
  },
  {
    bg: "from-rose-500 to-pink-500",
    chip: "bg-rose-500/15 text-rose-200 border-rose-500/30",
    solid: "#f43f5e",
  },
  {
    bg: "from-indigo-500 to-blue-500",
    chip: "bg-indigo-500/15 text-indigo-200 border-indigo-500/30",
    solid: "#6366f1",
  },
  {
    bg: "from-lime-500 to-green-500",
    chip: "bg-lime-500/15 text-lime-200 border-lime-500/30",
    solid: "#84cc16",
  },
  {
    bg: "from-purple-500 to-violet-600",
    chip: "bg-purple-500/15 text-purple-200 border-purple-500/30",
    solid: "#a855f7",
  },
];

export function tenantColor(id) {
  if (!id) return TENANT_PALETTE[0];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return TENANT_PALETTE[h % TENANT_PALETTE.length];
}

export function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

/**
 * Computes usage indicator for a given counter against a plan limit.
 *
 * @param current  current count (e.g. tenant.client_count)
 * @param limit    plan limit (0 / null / undefined = unlimited)
 * @returns        { text, percent, level }
 *                  level: "ok" | "warn" | "danger" | "unlimited"
 */
export function usageStatus(current, limit) {
  const cur = Number(current) || 0;
  if (!limit || limit <= 0) {
    return { text: `${cur} · ilimitado`, percent: 0, level: "unlimited" };
  }
  const lim = Number(limit);
  const pct = Math.min(100, Math.round((cur / lim) * 100));
  let level = "ok";
  if (cur >= lim) level = "danger";
  else if (cur >= lim - 1 || pct >= 90) level = "danger";
  else if (pct >= 75) level = "warn";
  return { text: `${cur}/${lim}`, percent: pct, level };
}

const LEVEL_CLASS = {
  ok: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
  warn: "bg-amber-500/15 text-amber-200 border-amber-500/40",
  danger: "bg-red-500/20 text-red-200 border-red-500/40",
  unlimited: "bg-slate-700/40 text-slate-300 border-white/10",
};

export function usageChipClass(level) {
  return LEVEL_CLASS[level] || LEVEL_CLASS.ok;
}
